import { formatCurrency, formatUnit } from '@/common/utils/formatters'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { useMemo, useState } from 'react'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { UpdateExecutionEntryEstablishmentAcceptValidationSchema } from '../../../../../schemas/updateExecutionEntryEstablishmentAccept.schema'

type FormSchema = UpdateExecutionEntryEstablishmentAcceptValidationSchema
type ModificationItem = FormSchema['pieces'][number]

export type ProposalFieldName = `pieces.${number}` | `activities.${number}`

interface UseProposalModificationParams {
  fieldName: ProposalFieldName
  unitPrice: string
  warrantyInDays: number
  quantity: number | string
  totalValue: string
}

export const useProposalModification = ({
  fieldName,
  unitPrice,
  warrantyInDays,
  quantity,
  totalValue,
}: UseProposalModificationParams) => {
  const {
    control,
    setValue,
    formState: { disabled },
  } = useFormContext<FormSchema>()

  const itemFormState = useWatch({ control, name: fieldName as Path<FormSchema> }) as ModificationItem | undefined

  const [isOpen, setIsOpen] = useState(false)
  const [localUnitPrice, setLocalUnitPrice] = useState('')
  const [localWarranty, setLocalWarranty] = useState('')

  const originalUnitPrice = Number(unitPrice) || 0
  const originalWarranty = warrantyInDays
  const quantityNumber = Number(quantity) || 0
  const originalTotalValue = Number(totalValue) || 0

  const newUnitPriceNumber = itemFormState?.new_unit_price ? unFormatCurrency(itemFormState.new_unit_price) : null
  const newWarrantyNumber = itemFormState?.new_warranty_in_days
    ? unFormatUnknownUnit(itemFormState.new_warranty_in_days)
    : null

  const isUnitPriceModified = newUnitPriceNumber !== null && newUnitPriceNumber !== originalUnitPrice
  const isWarrantyModified = newWarrantyNumber !== null && newWarrantyNumber !== originalWarranty

  const displayNewUnitPrice = isUnitPriceModified ? newUnitPriceNumber : null
  const displayNewWarranty = isWarrantyModified ? newWarrantyNumber : null

  const displayNewTotalValue = useMemo(() => {
    if (!isUnitPriceModified || newUnitPriceNumber === null) return null
    return newUnitPriceNumber * quantityNumber
  }, [isUnitPriceModified, newUnitPriceNumber, quantityNumber])

  const unitPricePercentage = useMemo(() => {
    if (displayNewUnitPrice === null || originalUnitPrice === 0) return null
    return ((displayNewUnitPrice - originalUnitPrice) / originalUnitPrice).toString()
  }, [displayNewUnitPrice, originalUnitPrice])

  const totalValuePercentage = useMemo(() => {
    if (displayNewTotalValue === null || originalTotalValue === 0) return null
    return ((displayNewTotalValue - originalTotalValue) / originalTotalValue).toString()
  }, [displayNewTotalValue, originalTotalValue])

  const previewTotalValue = useMemo(() => {
    const parsed = unFormatCurrency(localUnitPrice)
    return (parsed || 0) * quantityNumber
  }, [localUnitPrice, quantityNumber])

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setLocalUnitPrice(itemFormState?.new_unit_price || formatCurrency(originalUnitPrice))
      setLocalWarranty(itemFormState?.new_warranty_in_days || formatUnit(originalWarranty, 'dia(s)'))
    }
    setIsOpen(open)
  }

  const onConfirm = () => {
    setValue(`${fieldName}.new_unit_price` as Path<FormSchema>, localUnitPrice as never)
    setValue(`${fieldName}.new_warranty_in_days` as Path<FormSchema>, localWarranty as never)
    setIsOpen(false)
  }

  return {
    disabled,
    isOpen,
    handleOpenChange,
    localUnitPrice,
    setLocalUnitPrice,
    localWarranty,
    setLocalWarranty,
    previewTotalValue,
    onConfirm,
    originalUnitPrice,
    displayNewUnitPrice,
    unitPricePercentage,
    originalTotalValue,
    displayNewTotalValue,
    totalValuePercentage,
    originalWarranty,
    displayNewWarranty,
  }
}
